import { acceptHMRUpdate, defineStore } from "pinia";
import type { Task } from "~/types/task";
import type { Section } from "~/types/section";
import {
  normalizeTask,
  normalizeTasks,
  normalizeSection,
  normalizeSections,
} from "~/utils/task-normalizer";
import { TASK_CONSTANTS } from "~/constants/task";
import { DONE_STATUSES } from "~/utils/chartUtils";

// Token cache for collaboration tokens (phase 4)
const collabTokensByTaskId = new Map<
  number,
  { token: string; roomId: string; expiresAt: number }
>();

// ============= TYPES =============

interface FilterState {
  status: string[];
  assigned_toMe: boolean;
  dueThisWeek: boolean;
  dueNextWeek: boolean;
  dueToday: boolean;
  priority: string[];
  assigned_to: string[];
  types: Task["type"][];
}

interface SortRule {
  field: string;
  direction: "asc" | "desc";
}

interface QueryParams {
  q?: string;
  family_group_id?: number | string;
  sectionIds?: string | number[];
  sectionId?: number | string;
  status?: string;
  priority?: string;
  assigned_to?: number | string;
  dueToday?: boolean;
  dueThisWeek?: boolean;
  dueNextWeek?: boolean;
  types?: Task["type"][];
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  limit?: number | string;
  offset?: number;
  [key: string]: string | number | string[] | number[] | boolean | undefined;
}

// ============= STORE =============

export const useTaskStoreV2 = defineStore("task-v2", () => {
  const auth = useAuthStore();
  const toast = useToast();

  // ============= STATE =============

  // Normalized data
  const tasksById = ref<Map<number, Partial<Task>>>(new Map());
  const sectionsById = ref<Map<number, Section>>(new Map());

  // Order tracking
  const sectionIds = ref<number[]>([]);
  const taskIdsBySection = ref<Map<number | null, number[]>>(new Map());

  // Metadata
  const taskCountsBySection = ref<Map<number, number>>(new Map());
  const fullyLoadedSections = ref<Set<number>>(new Set());

  // Loading states
  const isInitialLoading = ref(false);
  const sectionLoadingStates = ref<Map<number, boolean>>(new Map());
  const lastError = ref<string | null>(null);

  // Filter & Sort
  const filters = ref<FilterState>({
    status: [],
    assigned_toMe: false,
    dueThisWeek: false,
    dueNextWeek: false,
    dueToday: false,
    priority: [],
    assigned_to: [],
    types: [],
  });

  const sortRule = ref<SortRule | null>(null);

  // Computed: Check if any filters are currently active
  const hasActiveFilters = computed((): boolean => {
    const f = filters.value;
    return (
      f.status.length > 0 ||
      f.assigned_toMe ||
      f.dueThisWeek ||
      f.dueNextWeek ||
      f.dueToday ||
      f.priority.length > 0 ||
      f.assigned_to.length > 0 ||
      f.types.length > 0
    );
  });

  // ============= GETTERS =============

  const getMyTasks = computed((): Partial<Task>[] => {
    return Array.from(tasksById.value.values()).filter(
      (task) => task.assigned_to === auth.user?.id,
    );
  });

  const getReviewTasks = computed((): Partial<Task>[] => {
    return Array.from(tasksById.value.values()).filter((task) => task.status === "in_review");
  });

  const getTask = (id: number): Partial<Task> | undefined => {
    return tasksById.value.get(id);
  };

  const getSection = (id: number): Section | undefined => {
    return sectionsById.value.get(id);
  };

  const getTasksForSection = (sectionId: number | null): Task[] => {
    const ids = taskIdsBySection.value.get(sectionId) ?? [];
    return ids.map((id) => tasksById.value.get(id)).filter((t): t is Task => t !== undefined);
  };

  const getAllTasks = computed((): Partial<Task>[] => {
    return Array.from(tasksById.value.values());
  });

  const getAllSections = computed((): Section[] => {
    return sectionIds.value
      .map((id) => sectionsById.value.get(id))
      .filter((s): s is Section => s !== undefined);
  });

  const getUnassignedTasks = computed((): Task[] => {
    return getTasksForSection(null);
  });

  const getSectionTaskCount = (sectionId: number): number => {
    return taskCountsBySection.value.get(sectionId) ?? 0;
  };

  const getLoadedTaskCountForSection = (sectionId: number): number => {
    return (taskIdsBySection.value.get(sectionId) ?? []).length;
  };

  const hasMoreTasksToLoad = (sectionId: number): boolean => {
    const total = getSectionTaskCount(sectionId);
    const loaded = getLoadedTaskCountForSection(sectionId);
    const hasMore = loaded < total;
    // Debug logging (can be removed later)
    // if (import.meta.env.DEV) {
    //   console.log(
    //     `[hasMore] Section ${sectionId}: loaded=${loaded}, total=${total}, hasMore=${hasMore}`,
    //   );
    // }
    return hasMore;
  };

  const isSectionFullyLoaded = (sectionId: number): boolean => {
    return !hasMoreTasksToLoad(sectionId);
  };

  // ============= LOCAL MUTATIONS =============

  const setTask = (task: Partial<Task>): void => {
    tasksById.value.set(task.id, task);
  };

  const addTaskToSection = (task: Partial<Task>): void => {
    setTask(task);
    const sectionKey = task.section_id;
    const currentIds = taskIdsBySection.value.get(sectionKey) ?? [];
    if (!currentIds.includes(task.id)) {
      taskIdsBySection.value.set(sectionKey, [...currentIds, task.id]);
    }
  };

  const removeTaskFromSection = (taskId: number, sectionId: number | null): void => {
    const currentIds = taskIdsBySection.value.get(sectionId) ?? [];
    taskIdsBySection.value.set(
      sectionId,
      currentIds.filter((id) => id !== taskId),
    );
  };

  const moveTaskBetweenSections = (
    taskId: number,
    fromSectionId: number | null,
    toSectionId: number | null,
  ): void => {
    removeTaskFromSection(taskId, fromSectionId);
    const task = tasksById.value.get(taskId);
    if (task) {
      task.section_id = toSectionId;
      // addTaskToSection(task);
    }
  };

  const updateTaskLocal = (taskId: number, changes: Partial<Task>): Partial<Task> | null => {
    const task = tasksById.value.get(taskId);
    if (!task) return null;

    const updated = { ...task, ...changes };

    // Derive `completed_at` from status so a task that reaches a done status
    // doesn't vanish from the recent-done filter before the server responds —
    // and so a Transmit broadcast carrying the status (but not the timestamp)
    // still restores the row. Mirrors the backend: set on entering a done
    // status, cleared on leaving.
    if (DONE_STATUSES.includes(updated.status)) {
      if (updated.completed_at === null || updated.completed_at === undefined) {
        updated.completed_at = new Date().toISOString();
      }
    } else {
      updated.completed_at = null;
    }

    tasksById.value.set(taskId, updated);

    // Handle section change
    if (changes.section_id !== undefined && changes.section_id !== task.section_id) {
      moveTaskBetweenSections(taskId, task.section_id, changes.section_id);
    }

    return updated;
  };

  const removeTaskLocal = (taskId: number): Partial<Task> | null => {
    const task = tasksById.value.get(taskId);
    if (!task) return null;

    tasksById.value.delete(taskId);
    removeTaskFromSection(taskId, task.section_id);

    return task;
  };

  const setSection = (section: Section): void => {
    sectionsById.value.set(section.id, section);
    if (!sectionIds.value.includes(section.id)) {
      sectionIds.value.push(section.id);
    }
  };

  const removeSectionLocal = (sectionId: number): Section | null => {
    const section = sectionsById.value.get(sectionId);
    if (!section) return null;

    sectionsById.value.delete(sectionId);
    sectionIds.value = sectionIds.value.filter((id) => id !== sectionId);
    taskIdsBySection.value.delete(sectionId);
    taskCountsBySection.value.delete(sectionId);
    fullyLoadedSections.value.delete(sectionId);

    return section;
  };

  // ============= API ACTIONS WITH OPTIMISTIC UPDATES =============

  const getDashboardSummary = async (query?: QueryParams) => {
    try {
      // Validate family_group_id if provided
      if (
        query?.family_group_id === undefined ||
        query?.family_group_id === null ||
        query?.family_group_id === "undefined"
      ) {
        console.warn(
          "getDashboardSummary called with invalid family_group_id:",
          query?.family_group_id,
        );
        return {
          userTasks: {
            overdue: 0,
            dueThisWeek: 0,
            completedThisWeek: 0,
            totalAssigned: 0,
            completed: 0,
            completionRate: 0,
          },
          recentTasks: [],
        };
      }

      const resp = await auth.api<{
        userTasks: {
          overdue: number;
          dueThisWeek: number;
          completedThisWeek: number;
          totalAssigned: number;
          completed: number;
          completionRate: number;
        };
        recentTasks: Task[];
      }>("get", "/task/dashboard-summary", {
        queryString: query,
      });
      return resp;
    } catch (error: unknown) {
      console.error("Error fetching dashboard summary:", error);
      const description =
        (error as { data?: { message?: string }; message?: string })?.data?.message ||
        (error as { message?: string })?.message ||
        "Could not fetch dashboard summary.";
      toast.add({
        description,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      throw new Error(description);
    }
  };

  const fetchInitialData = async (family_group_id: number | string): Promise<void> => {
    if (!family_group_id || family_group_id === "undefined") {
      console.warn("fetchInitialData called with invalid family_group_id");
      return;
    }

    isInitialLoading.value = true;
    lastError.value = null;

    try {
      const [sectionsResp, tasksResp] = await Promise.all([
        auth.api<{ data: any[] }>("get", "/section/index", {
          queryString: {
            family_group_id: family_group_id,
            limit: TASK_CONSTANTS.MAX_SECTIONS_PER_PAGE,
          },
        }),
        auth.api<{ data: any[]; meta: { taskCountsBySection: Record<number, number> } }>(
          "get",
          "/task/initial-tasks",
          {
            queryString: {
              family_group_id: family_group_id,
              limit: TASK_CONSTANTS.INITIAL_TASKS_PER_SECTION,
              sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
              order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
            },
          },
        ),
      ]);

      // Clear existing data
      tasksById.value.clear();
      sectionsById.value.clear();
      sectionIds.value = [];
      taskIdsBySection.value.clear();
      taskCountsBySection.value.clear();
      fullyLoadedSections.value.clear();

      // Populate sections
      const normalizedSections = normalizeSections(sectionsResp.data);
      normalizedSections.forEach((section) => {
        setSection(section);
        taskIdsBySection.value.set(section.id, []);
      });

      // Populate task counts
      const counts = tasksResp.meta?.taskCountsBySection ?? {};
      Object.entries(counts).forEach(([id, count]) => {
        taskCountsBySection.value.set(Number(id), count);
      });

      // Populate tasks
      const normalizedTasks = normalizeTasks(tasksResp.data);
      normalizedTasks.forEach((task) => {
        addTaskToSection(task);
      });

      // Mark sections as fully loaded if they have all tasks
      sectionIds.value.forEach((sectionId) => {
        const total = taskCountsBySection.value.get(sectionId) ?? 0;
        const loaded = (taskIdsBySection.value.get(sectionId) ?? []).length;
        if (loaded >= total) {
          fullyLoadedSections.value.add(sectionId);
        }
      });
    } catch (error: any) {
      const message = error?.data?.message ?? error?.message ?? "Failed to load data";
      lastError.value = message;
      toast.add({
        description: message,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
      throw error;
    } finally {
      isInitialLoading.value = false;
    }
  };

  const fetchInitialDataWithQuery = async (
    family_group_id: number | string,
    filters?: FilterState,
  ): Promise<void> => {
    isInitialLoading.value = true;

    // console.log("Initial Query, filters:", filters);
    try {
      const queryString: QueryParams = {
        family_group_id,
        limit: TASK_CONSTANTS.INITIAL_TASKS_PER_SECTION,
        sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
        order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
      };

      // 🔥 translate filters → query params (reuse logic)
      if (filters?.status.length) queryString.status = filters.status.join(",");
      if (filters?.priority.length) queryString.priority = filters.priority.join(",");
      if (filters?.types.length) queryString.types = filters.types as Task["type"][];
      if (filters?.assigned_toMe && auth.user?.id) {
        queryString.assigned_to = auth.user.id;
      }
      if (filters?.dueThisWeek) queryString.dueThisWeek = true;
      if (filters?.dueNextWeek) queryString.dueNextWeek = true;
      if (filters?.dueToday) queryString.dueToday = true;

      // console.log("currentFilters", filters);/
      // console.log("queryString", queryString);

      const tasksResp = await auth.api("get", "/task/initial-tasks", {
        queryString,
      });

      // console.log("tasksResp=", tasksResp, tasksResp);
      // Clear existing data
      tasksById.value.clear();
      // sectionsById.value.clear();
      // sectionIds.value = [];
      taskIdsBySection.value.clear();
      taskCountsBySection.value.clear();
      fullyLoadedSections.value.clear();

      // Initialize ALL sections to 0 first to ensure proper reactivity
      // This ensures sections with no matching tasks get explicitly set to 0
      sectionIds.value.forEach((sectionId) => {
        taskCountsBySection.value.set(sectionId, 0);
        taskIdsBySection.value.set(sectionId, []);
      });

      // Populate task counts from backend (overwrites 0s for sections with tasks)
      const counts = tasksResp.meta?.taskCountsBySection ?? {};
      Object.entries(counts).forEach(([id, count]) => {
        taskCountsBySection.value.set(Number(id), count);
      });

      // Populate tasks
      const normalizedTasks = normalizeTasks(tasksResp.data);
      normalizedTasks.forEach((task) => {
        addTaskToSection(task);
      });

      // Mark sections as fully loaded if they have all tasks
      sectionIds.value.forEach((sectionId) => {
        const total = taskCountsBySection.value.get(sectionId) ?? 0;
        const loaded = (taskIdsBySection.value.get(sectionId) ?? []).length;
        if (loaded >= total) {
          fullyLoadedSections.value.add(sectionId);
        }
      });
    } finally {
      isInitialLoading.value = false;
    }
  };

  const fetchMyTasks = async (family_group_id: number, assigned_to: number): Promise<void> => {
    if (!family_group_id || !assigned_to) {
      console.warn("fetchMyTasks called with invalid family_group_id or assigned_to");
      return;
    }
    try {
      // Use dedicated endpoint that returns all user's tasks without section filtering
      const response = await auth.api<{ meta: any; data: any[] }>("get", "/task/my-tasks", {
        queryString: {
          family_group_id: family_group_id,
          limit: 200,
          sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
          order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
        },
      });

      // Access the data array from the response
      const tasksArray = response.data || [];
      const normalizedTasks = normalizeTasks(tasksArray);

      // Use addTaskToSection to properly add tasks with reactivity
      normalizedTasks.forEach((task) => {
        if (!tasksById.value.has(task.id)) {
          addTaskToSection(task);
        }
      });
    } catch (error: any) {
      console.error("fetchMyTasks error:", error);
      toast.add({
        description: "Failed to load my tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
    } finally {
      // TODO: See that later
    }
  };

  const fetchReviewTasks = async (family_group_id: number | string): Promise<void> => {
    if (!family_group_id) {
      console.warn("fetchReviewTasks called with invalid family_group_id");
      return;
    }
    try {
      const response = await auth.api<{ meta: any; data: any[] }>("get", "/task/index", {
        queryString: {
          family_group_id,
          status: "in_review",
          limit: 200,
          sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
          order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
        },
      });

      const tasksArray = response.data || [];
      const normalizedTasks = normalizeTasks(tasksArray);

      normalizedTasks.forEach((task) => {
        if (!tasksById.value.has(task.id)) {
          setTask(task);
        }
      });
    } catch (error: any) {
      console.error("fetchReviewTasks error:", error);
      toast.add({
        description: "Failed to load review tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
    }
  };

  /**
   * Fetch tasks for a specific resident and add them to the store.
   * This is used by profile overview pages to ensure tasks are in the store
   * for TaskRowLite/TaskModal to manage updates.
   * Also fetches sections if not already loaded, so SectionPicker works in TaskModal.
   */
  const fetchResidentTasks = async (params: {
    residentId: number;
    familyGroupId: number | string;
    limit?: number;
  }): Promise<Task[]> => {
    if (!params.residentId || !params.familyGroupId) {
      console.warn("fetchResidentTasks called with invalid parameters");
      return [];
    }

    try {
      // Fetch sections if not already loaded (needed for SectionPicker in TaskModal)
      const sectionsPromise =
        sectionsById.value.size === 0
          ? auth.api<{ data: any[] }>("get", "/section/index", {
              queryString: {
                family_group_id: params.familyGroupId,
                limit: TASK_CONSTANTS.MAX_SECTIONS_PER_PAGE,
              },
            })
          : Promise.resolve(null);

      const tasksPromise = auth.api<{ meta: any; data: any[] }>("get", "/task/index", {
        queryString: {
          family_group_id: params.familyGroupId,
          resident_id: params.residentId,
          limit: params.limit ?? 100,
          sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
          order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
        },
      });

      const [sectionsResp, tasksResp] = await Promise.all([sectionsPromise, tasksPromise]);

      // Populate sections if fetched
      if (sectionsResp?.data) {
        const normalizedSections = normalizeSections(sectionsResp.data);
        normalizedSections.forEach((section) => {
          setSection(section);
          if (!taskIdsBySection.value.has(section.id)) {
            taskIdsBySection.value.set(section.id, []);
          }
        });
      }

      const tasksArray = tasksResp.data || [];
      const normalizedTasks = normalizeTasks(tasksArray);

      // Add tasks to store if not already present
      normalizedTasks.forEach((task) => {
        if (!tasksById.value.has(task.id)) {
          setTask(task);
        }
      });

      return normalizedTasks;
    } catch (error: any) {
      console.error("fetchResidentTasks error:", error);
      toast.add({
        description: "Failed to load resident tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
      return [];
    }
  };

  /**
   * Fetch tasks for a specific child and add them to the store.
   * This is used by profile overview pages to ensure tasks are in the store
   * for TaskRowLite/TaskModal to manage updates.
   * Also fetches sections if not already loaded, so SectionPicker works in TaskModal.
   */
  const fetchChildTasks = async (params: {
    childId: number;
    familyGroupId: number | string;
    limit?: number;
  }): Promise<Task[]> => {
    if (!params.childId || !params.familyGroupId) {
      console.warn("fetchChildTasks called with invalid parameters");
      return [];
    }

    try {
      // Fetch sections if not already loaded (needed for SectionPicker in TaskModal)
      const sectionsPromise =
        sectionsById.value.size === 0
          ? auth.api<{ data: any[] }>("get", "/section/index", {
              queryString: {
                family_group_id: params.familyGroupId,
                limit: TASK_CONSTANTS.MAX_SECTIONS_PER_PAGE,
              },
            })
          : Promise.resolve(null);

      const tasksPromise = auth.api<{ meta: any; data: any[] }>("get", "/task/index", {
        queryString: {
          family_group_id: params.familyGroupId,
          child_id: params.childId,
          limit: params.limit ?? 100,
          sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
          order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
        },
      });

      const [sectionsResp, tasksResp] = await Promise.all([sectionsPromise, tasksPromise]);

      // Populate sections if fetched
      if (sectionsResp?.data) {
        const normalizedSections = normalizeSections(sectionsResp.data);
        normalizedSections.forEach((section) => {
          setSection(section);
          if (!taskIdsBySection.value.has(section.id)) {
            taskIdsBySection.value.set(section.id, []);
          }
        });
      }

      const tasksArray = tasksResp.data || [];
      const normalizedTasks = normalizeTasks(tasksArray);

      // Add tasks to store if not already present
      normalizedTasks.forEach((task) => {
        if (!tasksById.value.has(task.id)) {
          setTask(task);
        }
      });

      return normalizedTasks;
    } catch (error: any) {
      console.error("fetchChildTasks error:", error);
      toast.add({
        description: "Failed to load child tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
      return [];
    }
  };

  /**
   * Fetch tasks filtered by type and optional timeframe (days)
   * Useful for specialized views like safeguarding that need specific task types
   */
  const fetchTasksByType = async (params: {
    family_group_id: number | string;
    types: Task["type"][];
    days?: number;
    limit?: number;
    sort?: string;
    order?: "asc" | "desc";
  }): Promise<Task[]> => {
    if (!params.family_group_id || params.family_group_id === "undefined") {
      console.warn("fetchTasksByType called with invalid family_group_id");
      return [];
    }

    try {
      const queryString: QueryParams = {
        family_group_id: params.family_group_id,
        type: params.types.join(","),
        limit: params.limit ?? 300,
        sort: params.sort ?? "created_at",
        order: params.order ?? "desc",
      };

      if (params.days) {
        queryString.days = params.days;
      }

      const response = await auth.api<{ meta: any; data: any[] }>("get", "/task/index", {
        queryString,
      });

      const tasksArray = response.data || [];
      const normalizedTasks = normalizeTasks(tasksArray);

      return normalizedTasks;
    } catch (error: any) {
      console.error("fetchTasksByType error:", error);
      toast.add({
        description: "Failed to load tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
      return [];
    }
  };

  /**
   * Fetch tasks overlapping a date range (calendar view) and MERGE them into
   * the store. Unlike fetchInitialData, this never clears tasksById and never
   * touches section mappings — calendar-loaded tasks must not appear in
   * section/board views. Returns the pagination meta so callers can page
   * through until the whole range is loaded.
   */
  const fetchTasksByDateRange = async (params: {
    family_group_id: number | string;
    from: string; // YYYY-MM-DD (inclusive)
    to: string; // YYYY-MM-DD (inclusive)
    page?: number;
    limit?: number;
  }): Promise<{ tasks: Task[]; meta: { lastPage?: number; total?: number } }> => {
    const queryString: QueryParams = {
      family_group_id: params.family_group_id,
      date_from: params.from,
      date_to: params.to,
      page: params.page ?? 1,
      limit: params.limit ?? 100,
      sort: "start_date",
      order: "asc",
    };

    const response = await auth.api<{ data: any[]; meta: any }>("get", "/task/index", {
      queryString,
    });

    const normalizedTasks = normalizeTasks(response.data);
    normalizedTasks.forEach((task) => setTask(task));

    return { tasks: normalizedTasks, meta: response.meta ?? {} };
  };

  const loadMoreTasksForSection = async (
    sectionId: number,
    family_group_id: number | string,
  ): Promise<void> => {
    if (fullyLoadedSections.value.has(sectionId)) return;
    if (sectionLoadingStates.value.get(sectionId)) return;

    sectionLoadingStates.value.set(sectionId, true);

    try {
      const alreadyLoaded = getLoadedTaskCountForSection(sectionId);
      const pageSize = 50;

      const queryString: QueryParams = {
        family_group_id,
        section_id: sectionId,
        limit: pageSize,
        offset: alreadyLoaded,
        sort: TASK_CONSTANTS.DEFAULT_SORT_FIELD,
        order: TASK_CONSTANTS.DEFAULT_SORT_DIRECTION,
      };

      // 🔥 translate filters → query params (reuse logic)
      if (filters.value.status.length) queryString.status = filters.value.status.join(",");
      if (filters.value.priority.length) queryString.priority = filters.value.priority.join(",");
      if (filters.value.types.length) queryString.type = filters.value.types.join(",");
      if (filters.value.assigned_toMe && auth.user?.id) {
        queryString.assigned_to = auth.user.id;
      }
      if (filters.value.dueThisWeek) queryString.dueThisWeek = true;
      if (filters.value.dueNextWeek) queryString.dueNextWeek = true;
      if (filters.value.dueToday) queryString.dueToday = true;

      const response = await auth.api<{
        data: any[];
        meta?: { total?: number; limit?: number | string; offset?: number; hasMore?: boolean };
      }>("get", "/task/section-tasks", {
        queryString,
      });

      const normalizedTasks = normalizeTasks(response.data);

      // Add only new tasks
      normalizedTasks.forEach((task) => {
        if (!tasksById.value.has(task.id)) {
          tasksById.value.set(task.id, task);
          addTaskToSection(task);
        }
      });

      // Use backend metadata to determine if all tasks are loaded
      const hasMoreFlag = response.meta?.hasMore;
      const totalFromMeta = response.meta?.total ?? null;
      const loadedNow = getLoadedTaskCountForSection(sectionId);

      // Determine if there are more tasks to load
      const hasMore =
        typeof hasMoreFlag === "boolean"
          ? hasMoreFlag
          : totalFromMeta !== null
            ? loadedNow < totalFromMeta
            : false;

      // Mark section as fully loaded if no more tasks
      if (!hasMore) {
        fullyLoadedSections.value.add(sectionId);
      }
    } catch (error: any) {
      toast.add({
        description: "Failed to load more tasks",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
    } finally {
      sectionLoadingStates.value.set(sectionId, false);
    }
  };

  const createTask = async (data: Partial<Task>): Promise<Task> => {
    // Create optimistic task with temporary ID
    const tempId = TASK_CONSTANTS.TEMP_ID_PREFIX * Date.now();
    const optimisticTask: Partial<Task> = {
      id: tempId,
      title: data.title ?? "",
      text: data.text ?? null,
      type: data.type ?? "task",
      status: data.status ?? TASK_CONSTANTS.DEFAULT_STATUS,
      priority: data.priority ?? TASK_CONSTANTS.DEFAULT_PRIORITY,
      section_id: data.section_id ?? null,
      order: data.order ?? 0,
      assigned_to: data.assigned_to ?? null,
      assigned_user: data.assigned_user ?? null,
      linked_to_id: null,
      linked_to_type: null,
      child_id: null,
      resident_id: null,
      start_date: data.start_date ?? null,
      end_date: data.end_date ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      section: null,
      family_group: data.family_group ?? null,
      family_group_id: data.family_group_id ?? null,
      recurrence_pattern: null,
      recurrence_start_at: null,
      recurrence_end_at: null,
      parent_task_id: null,
    };

    // Optimistic add
    addTaskToSection(optimisticTask);

    try {
      const response = await auth.api<{ task: any; message: string }>("post", "/task/create", data);

      // Remove temp, add real
      removeTaskLocal(tempId);
      const realTask = normalizeTask(response.task);
      addTaskToSection(realTask);

      toast.add({
        title: "Success",
        description: response.message ?? "Task created",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1800,
      });

      return realTask;
    } catch (error: any) {
      // Rollback
      removeTaskLocal(tempId);

      const message = error?.data?.message ?? error?.message ?? "Failed to create task";
      toast.add({
        description: message,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      throw error;
    }
  };

  // Bulk-create N tasks in one request. Mirrors createTask() but builds one
  // optimistic task per linked_to item (or a single unlinked one when the
  // array is empty) so the UI updates immediately. Temp IDs are derived from
  // a single Date.now() base minus the per-item index so the full batch can
  // be rolled back deterministically on success or error.
  const createTasksBulk = async (data: {
    title: string;
    section_id: number | null;
    family_group_id?: number | null;
    type?: Task["type"];
    priority?: Task["priority"];
    text?: Record<string, any> | null;
    start_date?: string | null;
    end_date?: string | null;
    recurrence_pattern?: string | null;
    recurrence_start_at?: string | Date | null;
    recurrence_end_at?: string | Date | null;
    timezone?: string;
    assigned_to?: number | null;
    linked_to: Array<{ id: number; type: "resident" | "child" }>;
    append_linked_name?: boolean;
  }): Promise<Task[]> => {
    const links = data.linked_to ?? [];
    // Empty array → one unlinked task (matches backend behavior and the
    // single-create contract). Non-empty → one task per link.
    const slots = links.length === 0 ? [null] : links;

    const residentStore = useResidentStore();
    const childStore = useChildStore();

    const getLinkLabel = (link: { id: number; type: "resident" | "child" }): string => {
      if (link.type === "resident") {
        const resident = residentStore.activeResidents?.find((r) => r.id === link.id);
        return resident ? `${resident.forename || ""} ${resident.surname || ""}`.trim() : "";
      }
      // Safe: bulk-create links originate from the picker, which only lists
      // children in the current family group (childStore.activeChildren).
      const child = childStore.activeChildren?.find((c) => c.id === link.id);
      return child?.full_name || "";
    };

    const buildTitle = (link: { id: number; type: "resident" | "child" } | null): string => {
      if (!data.append_linked_name || !link) return data.title;
      const label = getLinkLabel(link);
      return label ? `${label} - ${data.title}` : data.title;
    };

    // Use a temp ID range that cannot collide with the single-task create
    // helper even if both fire in the same millisecond.
    const tempIdBase = -Date.now() * 1000;

    // Recurrence dates only matter when a pattern is set; clear them otherwise
    // so stale picker values don't leak into unlinked tasks.
    const hasRecurrence = !!data.recurrence_pattern;
    const normalizedRecurrenceStartAt =
      hasRecurrence && data.recurrence_start_at
        ? new Date(data.recurrence_start_at).toISOString()
        : null;
    const normalizedRecurrenceEndAt =
      hasRecurrence && data.recurrence_end_at
        ? new Date(data.recurrence_end_at).toISOString()
        : null;

    const tempTasks: Partial<Task>[] = slots.map((link, index) => ({
      id: tempIdBase - index,
      title: buildTitle(link),
      text: data.text ?? null,
      type: data.type ?? "task",
      status: TASK_CONSTANTS.DEFAULT_STATUS,
      priority: data.priority ?? TASK_CONSTANTS.DEFAULT_PRIORITY,
      section_id: data.section_id ?? null,
      order: 0,
      assigned_to: data.assigned_to ?? null,
      assigned_user: null,
      linked_to_id: link?.id ?? null,
      linked_to_type: link?.type ?? null,
      child_id: link?.type === "child" ? link.id : null,
      resident_id: link?.type === "resident" ? link.id : null,
      start_date: data.start_date ?? null,
      end_date: data.end_date ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      section: null,
      family_group: null,
      family_group_id: data.family_group_id ?? null,
      recurrence_pattern: data.recurrence_pattern ?? null,
      recurrence_start_at: normalizedRecurrenceStartAt,
      recurrence_end_at: normalizedRecurrenceEndAt,
      parent_task_id: null,
    }));

    // Optimistic add for the whole batch.
    tempTasks.forEach((t) => addTaskToSection(t));

    // Normalize dates to ISO strings before sending (the recurrence picker
    // emits Date objects, but the backend expects ISO strings or null).
    const payload: Record<string, any> = {
      ...data,
      recurrence_start_at: normalizedRecurrenceStartAt,
      recurrence_end_at: normalizedRecurrenceEndAt,
    };

    try {
      const response = await auth.api<{ tasks: any[]; count: number; message: string }>(
        "post",
        "/task/bulk-create/",
        payload,
      );

      // Replace every temp with the corresponding real task (order-preserving).
      // The backend builds the title when append_linked_name is true, so we
      // trust the response titles without double-prefixing.
      tempTasks.forEach((temp) => removeTaskLocal(temp.id as number));
      const realTasks = normalizeTasks(response.tasks ?? []);
      realTasks.forEach((task) => addTaskToSection(task));

      toast.add({
        title: "Success",
        description:
          response.message ??
          `${realTasks.length} task${realTasks.length === 1 ? "" : "s"} created`,
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1800,
      });

      return realTasks;
    } catch (error: any) {
      // Roll back the entire batch.
      tempTasks.forEach((temp) => removeTaskLocal(temp.id as number));

      const message = error?.data?.message ?? error?.message ?? "Failed to create tasks";
      toast.add({
        description: message,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      throw error;
    }
  };

  const fetchTask = async (taskId: number): Promise<Task> => {
    try {
      const response = await auth.api<Task>("get", `/task/show/${taskId}`);
      const task = normalizeTask(response);
      // Always store the fetched task in tasksById so it's available via
      // getTask() for in-place updates (e.g., the history page replaces a
      // row when the modal closes by looking it up in the store). We do NOT
      // add it to taskIdsBySection — archived tasks should not appear in
      // section/dashboard views, but they must be retrievable for viewing.
      setTask(task);
      return task;
    } catch (error: any) {
      toast.add({
        description: error?.data?.message || error?.message || "Failed to fetch task",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      if (error?.statusCode !== 403) throw error;
    }
  };

  const updateTask = async (
    taskId: number,
    changes: Partial<Task>,
    options: { silent?: boolean } = {},
  ): Promise<boolean> => {
    const task = tasksById.value.get(taskId);
    if (!task) return false;

    // Store previous state for rollback
    const previousState = { ...task };

    // Optimistic update
    // console.log("Updating task", taskId, "with changes", changes);
    updateTaskLocal(taskId, changes);

    try {
      await auth.api<{ task: any; message: string }>("put", "/task/update/", {
        id: taskId,
        ...changes,
      });

      return true;
    } catch (error: any) {
      // Rollback
      updateTaskLocal(taskId, previousState);

      if (!options.silent) {
        toast.add({
          description: "Failed to update task",
          color: "error",
          icon: "i-heroicons-exclamation-triangle",
          duration: 3700,
        });
      }
      return false;
    }
  };

  /**
   * Update task with keepalive flag for critical scenarios (page unload)
   * This uses fetch with keepalive to ensure the request completes even if the page closes
   */
  const updateTaskWithKeepalive = async (taskId: number, changes: Partial<Task>): Promise<void> => {
    const task = tasksById.value.get(taskId);
    if (!task) return;

    // Optimistic update
    updateTaskLocal(taskId, changes);

    // try {
    // Get the API base URL from auth store
    // const config = useRuntimeConfig();
    // const baseURL = config.public.apiBase || 'http://localhost:3333';

    // // Use fetch with keepalive instead of the auth.api wrapper
    // await fetch(`${baseURL}/task/update/`, {
    //   method: 'PUT',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${auth.token}`,
    //   },
    //   body: JSON.stringify({ id: taskId, ...changes }),
    //   keepalive: true, // Critical: keeps request alive after page unload
    // });
    // } catch (error) {
    //   // Silent fail - we're likely unloading anyway
    //   console.error('Failed to save with keepalive:', error);
    // }
  };

  const deleteTask = async (taskId: number): Promise<boolean> => {
    const task = tasksById.value.get(taskId);
    if (!task) return false;

    // Store for rollback
    const previousTask = { ...task };
    // const previousSectionId = task.section_id;

    // Optimistic remove
    removeTaskLocal(taskId);

    try {
      await auth.api<{ message: string }>("DELETE", "/task/delete/", { task_id: taskId });

      toast.add({
        title: "Success",
        description: "Task deleted",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1200,
      });

      return true;
    } catch (error: any) {
      // Rollback
      addTaskToSection(previousTask as Task);

      toast.add({
        description: "Failed to delete task",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      return false;
    }
  };

  // ============= ARCHIVE / UNARCHIVE =============

  /**
   * Derives whether a task has generated child instances by scanning the
   * loaded `tasksById` Map for any task whose `parent_task_id` matches.
   * Same-section children are always loaded with the section data, so this
   * is reliable for the common case. Cross-section children may not be loaded
   * — a false-negative falls back to the "Archive" button (safe default per
   * product decision #1: archive applies to ALL tasks).
   */
  const hasChildren = (taskId: number): boolean => {
    for (const task of tasksById.value.values()) {
      if (task.parent_task_id === taskId) return true;
    }
    return false;
  };

  /**
   * Soft-delete a task via the archive endpoint. Optimistic: marks the task
   * as archived and removes it from the section list immediately, then POSTs
   * to the backend. On error, the snapshot is restored.
   */
  const archiveTask = async (taskId: number): Promise<boolean> => {
    const task = tasksById.value.get(taskId);
    if (!task) return false;

    // Snapshot for rollback
    const previousTask = { ...task };

    // Optimistic: mark archived + remove from section list (task stays in tasksById
    // so deep-linked modals can still render it in read-only mode)
    updateTaskLocal(taskId, { archived: true, archived_at: new Date().toISOString() });
    removeTaskFromSection(taskId, task.section_id);

    try {
      await auth.api("POST", `/task/archive/${taskId}`);

      toast.add({
        title: "Success",
        description: "Task archived",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1200,
      });

      return true;
    } catch (error: any) {
      // Rollback: restore task data + re-add to section list
      addTaskToSection(previousTask as Task);

      toast.add({
        description: "Failed to archive task",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      return false;
    }
  };

  /**
   * Reverse an archive action. Optimistic: clears the archived flag and
   * re-adds the task to the section list, then POSTs to the backend. On
   * error, the snapshot is restored.
   */
  const unarchiveTask = async (taskId: number): Promise<boolean> => {
    const task = tasksById.value.get(taskId);
    if (!task) return false;

    // Snapshot for rollback
    const previousTask = { ...task };

    // Optimistic: clear archived + re-add to section list
    const updated = updateTaskLocal(taskId, { archived: false, archived_at: null });
    if (updated) {
      addTaskToSection(updated);
    }

    try {
      await auth.api("POST", `/task/unarchive/${taskId}`);

      toast.add({
        title: "Success",
        description: "Task restored",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1200,
      });

      return true;
    } catch (error: any) {
      // Rollback: restore archived state + remove from section list
      tasksById.value.set(taskId, previousTask as Task);
      removeTaskFromSection(taskId, previousTask.section_id);

      toast.add({
        description: "Failed to restore task",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      return false;
    }
  };

  const createSection = async (data: Partial<Section>): Promise<Section> => {
    try {
      const response = await auth.api<{ section: any; message: string }>(
        "post",
        "/section/create",
        data,
      );

      const section = normalizeSection(response.section);
      setSection(section);
      taskIdsBySection.value.set(section.id, []);
      taskCountsBySection.value.set(section.id, 0);

      toast.add({
        title: "Success",
        description: response.message ?? "Section created",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1700,
      });

      return section;
    } catch (error: any) {
      const message = error?.data?.message ?? error?.message ?? "Failed to create section";
      toast.add({
        description: message,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 1700,
      });
      throw error;
    }
  };

  const updateSection = async (sectionId: number, changes: Partial<Section>): Promise<boolean> => {
    const section = sectionsById.value.get(sectionId);
    if (!section) return false;

    const previousState = { ...section };

    // Optimistic update
    sectionsById.value.set(sectionId, { ...section, ...changes });

    try {
      await auth.api<{ section: any; message: string }>("put", "/section/update/", {
        id: sectionId,
        ...changes,
      });

      toast.add({
        title: "Success",
        description: "Section updated",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1700,
      });

      return true;
    } catch (error: any) {
      // Rollback
      sectionsById.value.set(sectionId, previousState);

      toast.add({
        description: "Failed to update section",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      return false;
    }
  };

  const deleteSection = async (sectionId: number): Promise<boolean> => {
    const section = sectionsById.value.get(sectionId);
    if (!section) return false;

    const previousSection = { ...section };
    const previousTaskIds = [...(taskIdsBySection.value.get(sectionId) ?? [])];

    // Optimistic remove
    removeSectionLocal(sectionId);

    try {
      await auth.api<{ message: string }>("delete", "/section/delete/", { sectionId });

      toast.add({
        title: "Success",
        description: "Section deleted",
        color: "success",
        icon: "i-heroicons-check-circle",
        duration: 1700,
      });

      return true;
    } catch (error: any) {
      // Rollback
      setSection(previousSection as Section);
      taskIdsBySection.value.set(sectionId, previousTaskIds);

      toast.add({
        description: "Failed to delete section, section needs to be empty first",
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
        duration: 3700,
      });
      return false;
    }
  };

  // ============= FILTER METHODS =============

  const fetchInitialDataWithFilters = async (family_group_id: number | string): Promise<void> => {
    return fetchInitialDataWithQuery(family_group_id, filters.value);
  };

  const setFilters = async (
    newFilters: Partial<FilterState>,
    options?: { family_group_id?: number | string },
  ): Promise<void> => {
    // Update filters state
    filters.value = { ...filters.value, ...newFilters };

    // Fetch tasks from server with new filters applied
    if (options?.family_group_id) {
      // console.log("fetching tasks with new filters", filters.value);
      await fetchInitialDataWithFilters(options.family_group_id);
    }
  };

  const clearFilters = async (options?: { family_group_id?: number | string }): Promise<void> => {
    filters.value = {
      status: [],
      assigned_toMe: false,
      dueThisWeek: false,
      dueNextWeek: false,
      dueToday: false,
      priority: [],
      assigned_to: [],
      types: [],
    };

    // Fetch tasks from server with cleared filters if family_group_id is provided
    if (options?.family_group_id) {
      await fetchInitialData(options.family_group_id);
    }
  };

  const setSortRule = (rule: SortRule | null): void => {
    sortRule.value = rule;
  };

  const clearSort = (): void => {
    sortRule.value = null;
  };

  // ============= COLLAB TOKEN CACHING (Phase 4) =============

  const getCollabToken = async (
    taskId: number,
    opts: { forceRefresh?: boolean } = {},
  ): Promise<{ token: string; roomId: string } | null> => {
    const { forceRefresh = false } = opts;
    const now = Date.now();
    const cacheEntry = collabTokensByTaskId.get(taskId);

    if (!forceRefresh && cacheEntry && cacheEntry.expiresAt > now) {
      return { token: cacheEntry.token, roomId: cacheEntry.roomId };
    }

    try {
      const res = await auth.api<{ token: string; roomId: string }>("POST", "/collab/token", {
        taskId,
      });
      collabTokensByTaskId.set(taskId, {
        token: res.token,
        roomId: res.roomId,
        expiresAt: now + 1_800_000,
      });
      return res;
    } catch {
      return null;
    }
  };

  // ============= RESET =============

  const reset = (): void => {
    tasksById.value.clear();
    sectionsById.value.clear();
    sectionIds.value = [];
    taskIdsBySection.value.clear();
    taskCountsBySection.value.clear();
    fullyLoadedSections.value.clear();
    isInitialLoading.value = false;
    sectionLoadingStates.value.clear();
    lastError.value = null;
    clearFilters();
    clearSort();
  };

  return {
    // State (readonly externally)
    tasksById: readonly(tasksById),
    sectionsById: readonly(sectionsById),
    sectionIds: readonly(sectionIds),
    isInitialLoading: readonly(isInitialLoading),
    lastError: readonly(lastError),
    filters: filters,
    sortRule: readonly(sortRule),
    hasActiveFilters,

    // Getters
    getMyTasks,
    getReviewTasks,
    getTask,
    getSection,
    getTasksForSection,
    getAllTasks,
    getAllSections,
    getUnassignedTasks,
    isSectionFullyLoaded,
    getSectionTaskCount,
    getLoadedTaskCountForSection,
    hasMoreTasksToLoad,

    // Local Actions
    addTaskToSection,
    updateTaskLocal,
    removeTaskLocal,
    removeTaskFromSection,
    removeSectionLocal,

    // Actions
    getDashboardSummary,
    fetchInitialData,
    loadMoreTasksForSection,
    createTask,
    createTasksBulk,
    fetchMyTasks,
    fetchReviewTasks,
    fetchResidentTasks,
    fetchChildTasks,
    // fetchTasks,
    fetchInitialDataWithQuery,
    fetchTasksByType,
    fetchTasksByDateRange,
    fetchTask,
    updateTask,
    updateTaskWithKeepalive,
    deleteTask,
    archiveTask,
    unarchiveTask,
    hasChildren,
    createSection,
    updateSection,
    deleteSection,

    // Filter/Sort
    setFilters,
    clearFilters,
    setSortRule,
    clearSort,

    // Utilities
    reset,
    getCollabToken,

    // For advanced usage (drag-drop reordering)
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTaskStoreV2, import.meta.hot));
}
